// About.tsx
import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaHeart,
  FaHandsHelping,
  FaGraduationCap,
  FaUsers,
  FaChartLine,
  FaLeaf,
  FaAward,
  FaGlobeAfrica,
  FaBuilding,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaArrowRight,
  FaUserFriends,
  FaBook,
  FaHandHoldingHeart,
  FaStethoscope,
  FaWater,
  FaMosque,
  FaSchool,
  FaUsersCog
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  // Adjust this value to push the hero background image up/down.
  // Examples: 'top center' | 'center 10%' | 'center -5%' | 'center 30%'
  // - Smaller/negative percentage moves the image up, larger moves it down.
  const HERO_BG_POSITION = 'center 30%';
  // Image galleries for different sections
  const educationImages = [
    '/aboutPics/library.jpg',
    
  ];

  const trainingImages = [
    '/aboutPics/IT training.jpg'
  ];

  const healthImages = [
    '/aboutPics/Tumu ambulance.jpg',
    '/aboutPics/Tumu ambulance2.JPG'
  ];

  const donationImages = [
    '/aboutPics/donations.jpg',
    '/aboutPics/donations 2.jpg',
    //'/aboutPics/saqafiya donation.jpg',
    '/aboutPics/saqafiya donation2.jpg',
    '/aboutPics/donations 3.jpg',
    '/aboutPics/donations 4.JPG',
    '/aboutPics/Iftar.jpg',
    '/aboutPics/islamic donation.JPG',
    '/aboutPics/renovation 1.JPG',
    '/aboutPics/renovation 2.JPG',
    '/aboutPics/renovation 3.JPG'
  ];

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const canNavigate = lightboxImages.length > 1;

  const openLightbox = (images: string[], index: number) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goPrev = () => {
    if (!canNavigate) return;
    setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
  };

  const goNext = () => {
    if (!canNavigate) return;
    setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
  };

  return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Banner */}
      <div className="relative h-96 md:h-[500px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover"
          style={{ backgroundImage: 'url(/AIFCC2.jpg)', backgroundPosition: HERO_BG_POSITION }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-transparent to-slate-900/60"></div>
        </div>
        
        <div className="relative z-10 h-full flex items-center">
          <div className="container mx-auto px-4">
            <motion.div 
              className="max-w-3xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                Our <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">Story</span>
              </h1>
              <p className="text-xl text-gray-200 mb-6">
                Transforming Lives, Building Futures Across Ghana and Beyond
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-cyan-300">
                  <div className="w-6 h-px bg-cyan-300"></div>
                  <span className="text-sm font-medium">EST. 2014</span>
                </div>
                {/* Centered metadata; hero action buttons below (left/right) */}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Hero action buttons: left -> Team, right -> Leadership */}
        <div className="absolute bottom-6 left-6 z-30">
          <Link
            to="/team"
            className="group inline-flex items-center gap-2 px-5 py-3 bg-white text-slate-800 rounded-full font-medium hover:bg-slate-100 transition-all duration-300 shadow-md"
          >
            <FaUserFriends className="w-4 h-4" />
            Meet Our Team
            <FaArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="absolute bottom-6 right-6 z-30">
          <Link
            to="/leadership"
            className="group inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full font-medium hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 shadow-md"
          >
            <FaUserFriends className="w-4 h-4" />
            Meet Our Leadership
            <FaArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <motion.div
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            aria-hidden="true"
          >
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2"></div>
          </motion.div>
        </div>
      </div>

      {/* Meet the Team Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="container mx-auto px-4 -mt-8 relative z-20 mb-12"
      >
      
      </motion.div>

      {/* Main Content */}
      <div className="py-8">
        <div className="container mx-auto px-4">
          {/* Our Story */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 text-cyan-700 rounded-full text-sm font-semibold mb-4">
                OUR JOURNEY
              </span>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">The Foundation Story</h2>
            </div>

            {/* Introduction */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <p className="text-lg text-slate-700 leading-relaxed">
                <strong className="text-2xl block mb-4 text-cyan-800">Alhaji Inusah Foundation Community Centre (AIFCC)</strong>
                The Alhaji Inusah Foundation Community Centre (AIFCC) is a registered charitable organisation established in honour of the late Alhaji Inusah Adamu, who passed away on 12th March 2014 in Obuasi, Ghana. At his funeral, to eulogize him, his children announced the establishment of the foundation to sustain and institutionalize his lifelong commitment to kindness, education, and service to humanity. The foundation is a family-based charity, established with seed funding from his children and immediate family members, and has since 2014 remained committed to humanitarian and community development initiatives.
              </p>
            </div>

            {/* Education Section with Images */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
                  <FaBook className="w-6 h-6 text-cyan-600" />
                </div>
                <h3 className="text-3xl font-bold text-slate-800">1. Education</h3>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:items-stretch">
                <div className="space-y-4 h-full">
                  <p className="text-lg text-slate-700 leading-relaxed">
                    Education remains a core pillar of AIFCC's mission. Prior to the introduction of Ghana's Free Senior High School policy, the foundation focused primarily on supporting brilliant but needy students by paying school fees and covering costs related to books, uniforms, and other academic necessities. Many beneficiaries of this support have successfully completed tertiary education and are now productively employed.
                  </p>
                  <p className="text-lg text-slate-700 leading-relaxed">
                    To further promote a culture of reading and lifelong learning, the foundation established a community library, which currently serves over 500 registered members. The library is actively used by pupils and students from primary through senior high school, providing access to learning materials, a conducive study environment, and academic support in a form of resident tutors who provide guidance on daily basis.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-6 h-full flex items-center">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="w-full rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                    onClick={() => openLightbox(educationImages, 0)}
                  >
                    <img
                      src={educationImages[0]}
                      alt="Education initiative"
                      className="w-full h-72 sm:h-80 lg:h-full object-cover"
                    />
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Training Section with Images */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <FaUsersCog className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-3xl font-bold text-slate-800">2. Training and Skills Development</h3>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <p className="text-lg text-slate-700 leading-relaxed">
                    AIFCC is committed to empowering community members through practical skills acquisition. The foundation offers ICT training, vocational skills development, and other capacity-building programs designed to enhance employability and entrepreneurship. These training initiatives equip beneficiaries with income-generating skills, promote self-reliance, and contribute to poverty reduction within the community.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6">
                  <div className="grid grid-cols-1 gap-4">
                    {trainingImages.map((img, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                        onClick={() => openLightbox(trainingImages, index)}
                      >
                        <img
                          src={img}
                          alt="IT Training Program"
                          className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Community Engagement Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <FaUsers className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-3xl font-bold text-slate-800">3. Community Engagement</h3>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:items-stretch">
                <div className="space-y-4 h-full">
                  <p className="text-lg text-slate-700 leading-relaxed">
                    The foundation actively promotes social cohesion and community well-being through continuous engagement initiatives. These include counseling services, mentorship, and the organisation of community events and activities that encourage interaction, inclusion, and personal development. Through these efforts, AIFCC provides a safe and supportive platform for dialogue, learning, and socialisation among community members.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 flex items-start justify-center">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-sm rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                    onClick={() => openLightbox(['/aboutPics/counselling.jpg'], 0)}
                  >
                    <img
                      src="/aboutPics/counselling.jpg"
                      alt="Community engagement counselling"
                      className="w-full h-56 sm:h-64 object-cover"
                    />
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Health Section with Images */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                  <FaStethoscope className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-3xl font-bold text-slate-800">4. Health</h3>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <p className="text-lg text-slate-700 leading-relaxed">
                    Health support is a vital aspect of AIFCC's humanitarian work. The foundation has assisted numerous community members in accessing medical care, including support for surgical procedures, medications, and general medical treatment. In July 2019, AIFCC donated a modern ambulance to the Tumu Government Hospital in the Upper West Region of Ghana, significantly enhancing emergency medical response in the area. Additionally, the foundation periodically organises medical outreach programs to extend healthcare services to underserved populations.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-6">
                  <div className="grid grid-cols-2 gap-4">
                    {healthImages.map((img, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                        onClick={() => openLightbox(healthImages, index)}
                      >
                        <img
                          src={img}
                          alt={`Health initiative ${index + 1}`}
                          className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Charitable Donations Section with Gallery */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <FaHandHoldingHeart className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-3xl font-bold text-slate-800">5. Charitable Donations and Humanitarian Support</h3>
              </div>
              
              <div className="space-y-6 mb-8">
                <p className="text-lg text-slate-700 leading-relaxed">
                  AIFCC has consistently provided financial and material support to religious and social welfare institutions. The foundation has supported several mosques in Obuasi and Accra, including the Obuasi Central Mosque, Wangara Central Mosque, and Accra Abattoir Mosque. It has also made substantial donations to orphanages and social care institutions, including Great Mission International (Accra), Adullam Children's Home and Orphanage (Obuasi), Mercy Social Centre (Accra), and the Islamic Foundation Charity in Obuasi.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl">
                    <FaWater className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-2">Water Access Projects</h4>
                      <p className="text-slate-700">
                        The foundation has undertaken the construction of boreholes to provide access to safe and potable water for the Obuasi Zongo community, as well as Saqafiyya Primary and Junior High School.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                    <FaSchool className="w-6 h-6 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-2">Educational Support</h4>
                      <p className="text-slate-700">
                        AIFCC has supported education through the provision of student desks and essential classroom furniture to Saqafiyya Junior High School and Primary School.
                      </p>
                    </div>
                  </div>
                </div>
                
                <p className="text-lg text-slate-700 leading-relaxed">
                  In addition, the foundation undertakes annual Ramadan humanitarian activities, during which it provides weekly Friday iftar meals throughout the holy month in Obuasi and Accra. Essential food items and other basic commodities are distributed to support needy Muslim families, alongside direct assistance to the less privileged during the Ramadan period.
                </p>
              </div>

              {/* Donation Gallery */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 mb-8">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {donationImages.map((img, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      className="aspect-square rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 group cursor-pointer"
                      onClick={() => openLightbox(donationImages, index)}
                    >
                      <img
                        src={img}
                        alt={`Donation activity ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* AIFCC Community Centre Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <FaBuilding className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-3xl font-bold text-slate-800">The AIFCC Community Centre</h3>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8">
                <p className="text-lg text-slate-700 leading-relaxed mb-4">
                  On the occasion of its 10th anniversary, the Alhaji Inusah Foundation Community Centre was officially opened in Obuasi to serve as a multi-purpose facility for the community. The centre functions as a hub for community engagement, educational programs, training sessions, events, and meetings. It also serves as a special venue for community groups and dignitaries visiting Obuasi.
                </p>
                <p className="text-lg text-slate-700 leading-relaxed">
                  Through these sustained initiatives, AIFCC continues to preserve and advance the legacy of Alhaji Inusah Adamu, ensuring that his values of compassion, service, and community upliftment remain impactful and enduring.
                </p>
              </div>
            </motion.div>

            {/* Call to Action - Meet the Team */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mt-16"
            >
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-10 text-white">
                <FaUserFriends className="w-16 h-16 mx-auto mb-6 text-cyan-300" />
                <h3 className="text-3xl text-white font-bold mb-4">Meet the Team Behind Our Impact</h3>
                <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
                  Discover the dedicated individuals and visionary leaders who continue to drive AIF's mission forward and honor Alhaji Inusah Adamu's legacy.
                </p>
                <Link 
                  to="/leadership"
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl font-bold hover:from-cyan-600 hover:to-blue-600 hover:scale-105 transition-all duration-300 shadow-lg"
                >
                  <span>Meet Our Leadership Team</span>
                  <FaArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

   

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-5xl px-4">
            <div className="relative overflow-hidden rounded-xl bg-black/50">
              <img
                src={lightboxImages[lightboxIndex]}
                alt="Gallery view"
                className="w-full h-[70vh] object-contain"
              />
            </div>

            {canNavigate && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goPrev();
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white w-10 h-10 rounded-full flex items-center justify-center border border-white/30 transition-all duration-300"
                  aria-label="Previous image"
                >
                  <FaArrowRight className="rotate-180" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goNext();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white w-10 h-10 rounded-full flex items-center justify-center border border-white/30 transition-all duration-300"
                  aria-label="Next image"
                >
                  <FaArrowRight />
                </button>
              </>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                closeLightbox();
              }}
              className="absolute right-3 top-3 bg-white/10 hover:bg-white/20 text-white w-9 h-9 rounded-full flex items-center justify-center border border-white/30 transition-all duration-300"
              aria-label="Close"
            >
              <span className="text-xl leading-none">×</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default About;
